/**
 * Types and rules behind the feature announcements.
 *
 * Kept apart from `announcements.ts` on purpose: that file should be nothing
 * but the announcements themselves, so changing a date or a sentence does not
 * mean scrolling past a hundred lines of interfaces.
 */

import type { ConsoleSection } from 'environments/environment.model';

/** Illustrations the dialog can play. */
export type WhatsNewVisual = 'autoscaling' | 'mcp';

/**
 * The four products, which are both a Payment product slug and a console
 * section — and, deliberately, also their own route segment.
 *
 * Narrowing to this instead of `string` is what lets an announcement's target
 * be handed to `isSectionEnabled()` without a cast.
 */
export type ProductSection = Extract<
  ConsoleSection,
  'voice' | 'rooms' | 'fleet' | 'cortex'
>;

/** A single screen of an announcement. */
export interface WhatsNewStep {
  title: string;
  /** Short supporting copy, or an empty string when the heading is enough. */
  body: string;
  /**
   * Three or four facts, rendered as a row of small cards.
   *
   * Short label, short line. This is the part people actually read — the body
   * above sets it up, the cards carry the substance.
   */
  facts?: { label: string; text: string }[];
  /** Animated illustration shown above the text. */
  visual: WhatsNewVisual;
}

/**
 * A feature worth interrupting someone for.
 *
 * A modal stops the reader on purpose, so this list stays short: big releases
 * only. Everything smaller belongs in the docs or a release note.
 */
export interface WhatsNewAnnouncement {
  /**
   * Stable identifier, persisted once the reader has seen it.
   *
   * Never reuse or rename an id — it would either re-show a known announcement
   * to everyone or silently hide a new one.
   */
  id: string;
  /** ISO date. Newest unseen announcement wins when several are pending. */
  publishedAt: string;
  /**
   * ISO date after which the dialog no longer opens by itself.
   *
   * A feature stops being news, but people keep signing in for the first time
   * long after a release — without this, somebody joining years later would be
   * interrupted by something everyone else has used since forever.
   *
   * Defaults to {@link DEFAULT_AUTO_OPEN_DAYS} after `publishedAt`. Setting a
   * date in the past is also the way to pull an announcement immediately.
   */
  expiresAt?: string;
  /** Shown in the dialog header, under the product name. */
  title: string;
  /** Optional release marker, such as beta. */
  badge?: string;
  /**
   * Which product this belongs to.
   *
   * Rendered as logo plus name in the header: the reader should know within a
   * second which part of the console just changed, without parsing a sentence.
   */
  product: { label: string; icon: string };
  /** Usually one screen. Keep multi-step announcements short. */
  steps: WhatsNewStep[];
  /** The one thing we want the reader to do next. */
  primaryAction: WhatsNewAction;
  /** The quieter second option, usually the documentation. */
  secondaryAction?: WhatsNewAction;
}

/** How long an announcement interrupts, when it does not say otherwise. */
export const DEFAULT_AUTO_OPEN_DAYS = 365;

/** The moment an announcement stops opening by itself. */
export function autoOpenUntil(announcement: WhatsNewAnnouncement): Date {
  if (announcement.expiresAt) return new Date(announcement.expiresAt);

  const until = new Date(announcement.publishedAt);
  until.setDate(until.getDate() + DEFAULT_AUTO_OPEN_DAYS);
  return until;
}

/** Whether an announcement may still open the dialog on its own. */
export function isAutoOpenActive(
  announcement: WhatsNewAnnouncement,
  now: Date = new Date(),
): boolean {
  return now < autoOpenUntil(announcement);
}

export interface WhatsNewAction {
  /** Label when the route below is actually reachable. */
  label: string;
  /**
   * Where the action leads. `product` lets a project-scoped route be resolved
   * against the reader's own projects — they may have none with that product,
   * or the build may not even ship it (see `whats-new.cta.ts`).
   */
  target:
    | {
        kind: 'product-route';
        product: ProductSection;
        /** Route segments below `/projects/:projectId`. */
        path: string[];
        /**
         * Label when no project has the product yet — the button then leads to
         * the product's activation page, and has to say so.
         */
        activateLabel: string;
      }
    | { kind: 'route'; commands: string[] }
    | { kind: 'url'; url: string };
}
